// Package Imports
import { create } from 'zustand'

// Module Imports
import type { TThemeName } from '@/modules/auth/auth.types'

type TToggleOrigin = { clientX: number; clientY: number }

type TThemeStore = {
  theme: TThemeName
  /** Set theme + persist to localStorage. */
  setTheme: (theme: TThemeName) => void
  /** Flip theme. Wraps `setTheme` with the view-transition circle reveal. */
  toggleTheme: (origin?: TToggleOrigin) => void
}

const STORAGE_KEY = 'qg-theme'

const isThemeName = (value: unknown): value is TThemeName =>
  value === 'klasik' || value === 'canli'

/** Read persisted theme; falls back to `klasik`. Safe in non-browser env. */
export const readStoredTheme = (): TThemeName => {
  if (typeof window === 'undefined') return 'klasik'
  const stored = window.localStorage.getItem(STORAGE_KEY)
  return isThemeName(stored) ? stored : 'klasik'
}

const applyDocumentTheme = (theme: TThemeName) => {
  if (typeof document === 'undefined') return
  document.documentElement.dataset.theme = theme
}

const persistTheme = (theme: TThemeName) => {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(STORAGE_KEY, theme)
}

export const useThemeStore = create<TThemeStore>((set, get) => ({
  theme: readStoredTheme(),

  setTheme: (next) => {
    if (get().theme === next) return
    applyDocumentTheme(next)
    persistTheme(next)
    set({ theme: next })
  },

  toggleTheme: (origin) => {
    const next: TThemeName = get().theme === 'klasik' ? 'canli' : 'klasik'

    const doc = document as Document & {
      startViewTransition?: (cb: () => void) => {
        ready: Promise<void>
        finished: Promise<void>
      }
    }

    const prefersReduced = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches

    if (!doc.startViewTransition || prefersReduced) {
      get().setTheme(next)
      return
    }

    const x = origin?.clientX ?? window.innerWidth / 2
    const y = origin?.clientY ?? window.innerHeight / 2
    const endRadius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y)
    )

    const transition = doc.startViewTransition(() => {
      get().setTheme(next)
    })

    transition.ready
      .then(() => {
        document.documentElement.animate(
          {
            clipPath: [
              `circle(0px at ${x}px ${y}px)`,
              `circle(${endRadius}px at ${x}px ${y}px)`,
            ],
          },
          {
            duration: 650,
            easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
            pseudoElement: '::view-transition-new(root)',
          }
        )
      })
      .catch((err) => {
        console.error('[themeStore.toggleTheme.transition]', err)
      })
  },
}))
